import { calculateDistanceInMeters } from "./geoUtils";

export interface GeoDecisionState {
  currentNeighborhood: string | null;
  currentMapCenter: { lat: number; lng: number } | null;
  currentRadius: number;
  visiblePropertiesCount: number;
  hasUserInteractedWithMap: boolean;
  isDragging: boolean;
  isGeoSearchActiveInUrl: boolean;
}

export interface GeoDecisionResult {
  shouldUseGeoSearch: boolean;
  shouldUpdateListOnly: boolean;
  reason: string;
  newRadius?: number;
}

export const GEO_DECISION_CONFIG = {
  RADIUS_STEP: 500,
  MAX_RADIUS: 10000,
  MIN_RADIUS: 500,
  DISTANCE_THRESHOLD_METERS: 800,
  DECISION_DEBOUNCE_MS: 800,
} as const;

const FORTALEZA_NEIGHBORHOOD_CENTERS: Record<string, { lat: number; lng: number }> = {
  Aldeota: { lat: -3.7319, lng: -38.5267 },
  Meireles: { lat: -3.725, lng: -38.515 },
  Varjota: { lat: -3.733, lng: -38.51 },
  Papicu: { lat: -3.741, lng: -38.49 },
  Cocó: { lat: -3.75, lng: -38.48 },
  "Dionísio Torres": { lat: -3.74, lng: -38.52 },
  Centro: { lat: -3.7275, lng: -38.5275 },
  Fátima: { lat: -3.745, lng: -38.53 },
  "Praia de Iracema": { lat: -3.72, lng: -38.53 },
  Guararapes: { lat: -3.755, lng: -38.51 },
  "Joaquim Távora": { lat: -3.735, lng: -38.505 },
};

export const NEIGHBORHOOD_CENTERS = FORTALEZA_NEIGHBORHOOD_CENTERS;

export function getNeighborhoodCenter(
  neighborhood: string
): { lat: number; lng: number } | null {
  const normalized = neighborhood.trim();
  if (FORTALEZA_NEIGHBORHOOD_CENTERS[normalized]) {
    return FORTALEZA_NEIGHBORHOOD_CENTERS[normalized];
  }

  const matchKey = Object.keys(FORTALEZA_NEIGHBORHOOD_CENTERS).find(
    (key) => key.toLowerCase() === normalized.toLowerCase()
  );

  return matchKey ? FORTALEZA_NEIGHBORHOOD_CENTERS[matchKey] : null;
}

export function calculateDistanceFromNeighborhood(
  neighborhoodCenter: { lat: number; lng: number } | null,
  currentCenter: { lat: number; lng: number }
): number {
  if (!neighborhoodCenter) {
    return 0;
  }

  return calculateDistanceInMeters(
    neighborhoodCenter.lat,
    neighborhoodCenter.lng,
    currentCenter.lat,
    currentCenter.lng
  );
}

export function decideGeoSearch(state: GeoDecisionState): GeoDecisionResult {
  const {
    currentNeighborhood,
    currentMapCenter,
    currentRadius,
    visiblePropertiesCount,
    hasUserInteractedWithMap,
    isDragging,
    isGeoSearchActiveInUrl,
  } = state;

  if (!hasUserInteractedWithMap) {
    return {
      shouldUseGeoSearch: false,
      shouldUpdateListOnly: false,
      reason: "User has not interacted with the map yet",
    };
  }

  if (isDragging) {
    return {
      shouldUseGeoSearch: false,
      shouldUpdateListOnly: false,
      reason: "User is dragging, waiting for drag end",
    };
  }

  if (isGeoSearchActiveInUrl) {
    return {
      shouldUseGeoSearch: true,
      shouldUpdateListOnly: true,
      reason: "Geo search already active in URL - list-only update",
    };
  }

  if (!currentNeighborhood) {
    return {
      shouldUseGeoSearch: true,
      shouldUpdateListOnly: false,
      reason: "No neighborhood selected, activating geo search",
    };
  }

  if (!currentMapCenter) {
    return {
      shouldUseGeoSearch: false,
      shouldUpdateListOnly: true,
      reason: "Map center unavailable, list-only bounds filter",
    };
  }

  const neighborhoodCenter = getNeighborhoodCenter(currentNeighborhood);
  const distance = calculateDistanceFromNeighborhood(
    neighborhoodCenter,
    currentMapCenter
  );

  if (distance > GEO_DECISION_CONFIG.DISTANCE_THRESHOLD_METERS) {
    const newRadius = Math.min(
      currentRadius + GEO_DECISION_CONFIG.RADIUS_STEP,
      GEO_DECISION_CONFIG.MAX_RADIUS
    );

    return {
      shouldUseGeoSearch: true,
      shouldUpdateListOnly: false,
      reason: `User left neighborhood (${Math.round(distance)}m from center)`,
      newRadius,
    };
  }

  if (
    visiblePropertiesCount === 0 &&
    currentRadius < GEO_DECISION_CONFIG.MAX_RADIUS
  ) {
    return {
      shouldUseGeoSearch: true,
      shouldUpdateListOnly: true,
      reason: "No visible properties, expanding radius client-side",
      newRadius: Math.min(
        currentRadius + GEO_DECISION_CONFIG.RADIUS_STEP,
        GEO_DECISION_CONFIG.MAX_RADIUS
      ),
    };
  }

  return {
    shouldUseGeoSearch: false,
    shouldUpdateListOnly: true,
    reason: "Inside neighborhood with visible properties",
  };
}

let lastDecision: GeoDecisionResult | null = null;
let lastDecisionTime = 0;
let lastDecisionKey = "";

function buildDecisionCacheKey(state: GeoDecisionState): string {
  return JSON.stringify({
    n: state.currentNeighborhood,
    c: state.currentMapCenter,
    r: state.currentRadius,
    v: state.visiblePropertiesCount,
    i: state.hasUserInteractedWithMap,
    d: state.isDragging,
    g: state.isGeoSearchActiveInUrl,
  });
}

export function getCachedGeoDecision(state: GeoDecisionState): GeoDecisionResult {
  const now = Date.now();
  const cacheKey = buildDecisionCacheKey(state);

  if (
    lastDecision &&
    cacheKey === lastDecisionKey &&
    now - lastDecisionTime < GEO_DECISION_CONFIG.DECISION_DEBOUNCE_MS
  ) {
    return lastDecision;
  }

  const decision = decideGeoSearch(state);
  lastDecision = decision;
  lastDecisionTime = now;
  lastDecisionKey = cacheKey;

  return decision;
}

export function resetGeoDecisionCache(): void {
  lastDecision = null;
  lastDecisionTime = 0;
  lastDecisionKey = "";
}
